import { useCallback, useEffect, useState } from 'react';
import { isTypingTarget } from '../utils/workflowBuilderUtils';

export const UTILITY_PANELS = {
  comments: 'comments',
  validation: 'validation',
  stats: 'stats',
  sticky: 'sticky',
  switcher: 'switcher',
  search: 'search',
  versions: 'versions',
  ai: 'ai',
};

export function useWorkflowUtilityPanels({ onExitStatsView } = {}) {
  const [activePanel, setActivePanel] = useState(null);
  const [statsViewActive, setStatsViewActive] = useState(false);
  const [stickyNotesVisible, setStickyNotesVisible] = useState(true);
  const [stickyPaletteOpen, setStickyPaletteOpen] = useState(false);
  const [highlightedStepId, setHighlightedStepId] = useState(null);

  const closePanel = useCallback(() => {
    setActivePanel(null);
    setStickyPaletteOpen(false);
  }, []);

  const togglePanel = useCallback((panelId) => {
    if (panelId === UTILITY_PANELS.stats) {
      setStatsViewActive((prev) => {
        const next = !prev;
        if (next) {
          setActivePanel(null);
          setStickyPaletteOpen(false);
        } else {
          onExitStatsView?.();
        }
        return next;
      });
      return;
    }

    if (panelId === UTILITY_PANELS.sticky) {
      setStickyPaletteOpen((prev) => {
        const next = !prev;
        if (next) {
          setActivePanel(null);
          setStatsViewActive(false);
        }
        return next;
      });
      return;
    }

    setStatsViewActive(false);
    setStickyPaletteOpen(false);
    setActivePanel((current) => (current === panelId ? null : panelId));
  }, [onExitStatsView]);

  const exitStatsView = useCallback(() => {
    setStatsViewActive(false);
    onExitStatsView?.();
  }, [onExitStatsView]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isTypingTarget(e.target)) return;

      const mod = e.metaKey || e.ctrlKey;

      if (e.key === 'Escape') {
        if (statsViewActive) {
          exitStatsView();
          return;
        }
        if (activePanel || stickyPaletteOpen) {
          closePanel();
        }
        return;
      }

      if (mod && e.shiftKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        togglePanel(UTILITY_PANELS.stats);
        return;
      }
      if (e.shiftKey && !mod && e.key.toLowerCase() === 's') {
        e.preventDefault();
        togglePanel(UTILITY_PANELS.sticky);
        return;
      }
      if (e.shiftKey && e.key.toLowerCase() === 'w') {
        e.preventDefault();
        togglePanel(UTILITY_PANELS.switcher);
        return;
      }
      if (e.shiftKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        togglePanel(UTILITY_PANELS.ai);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activePanel, stickyPaletteOpen, statsViewActive, togglePanel, closePanel, exitStatsView]);

  return {
    activePanel,
    statsViewActive,
    stickyNotesVisible,
    setStickyNotesVisible,
    stickyPaletteOpen,
    highlightedStepId,
    setHighlightedStepId,
    togglePanel,
    closePanel,
    exitStatsView,
    isBuilderReadOnly: statsViewActive,
  };
}
